import { Box, FormField, FormHelp, InlineFeedback, Input, Label, Textarea } from '@zillow/constellation-10';
import { useEffect, useMemo, useState } from 'react';
import formValidationUtils from '../../../../../utils/formValidationUtils';
import type { SavedReply } from '../../../../../hooks/useSavedReplies';
import { debounce } from '@zg-rentals/zdash';

const TITLE_FIELD_MAX_LENGTH = 80;
const MESSAGE_FIELD_MAX_LENGTH = 2000;
const TITLE_FIELD_HELP_TEXT = `Title will not be included in your message. Max length is ${TITLE_FIELD_MAX_LENGTH} characters.`;
const TITLE_FIELD_ERROR_TEXT = 'This title already exists. Enter a different title.';
const MESSAGE_FIELD_HELP_TEXT = `Max length is ${MESSAGE_FIELD_MAX_LENGTH} characters.`;

type SavedReplyEditFieldsProps = {
  editedSavedReply: SavedReply;
  setEditedSavedReply: React.Dispatch<React.SetStateAction<SavedReply>>;
  hasFormError: boolean;
  hasDuplicateTitle: (title: string) => boolean;
};

export default function SavedReplyEditFields({
  editedSavedReply,
  setEditedSavedReply,
  hasFormError,
  hasDuplicateTitle,
}: SavedReplyEditFieldsProps): JSX.Element {
  const [hasEmptyTitleError, setHasEmptyTitleError] = useState(false);
  const [hasEmptyMessageError, setHasEmptyMessageError] = useState(false);
  const [hasDuplicateTitleError, setHasDuplicateTitleError] = useState(false);
  const titleFieldText = hasDuplicateTitleError ? TITLE_FIELD_ERROR_TEXT : TITLE_FIELD_HELP_TEXT;

  // Debounce the duplicate title check to avoid running on every keystroke
  const debouncedDuplicateCheck = useMemo(
    () =>
      debounce((title: string) => {
        const isDuplicate = hasDuplicateTitle(title);
        setHasDuplicateTitleError(isDuplicate);
      }, 300),
    [hasDuplicateTitle],
  );

  // Check for duplicate when a reply is loaded for editing
  useEffect(() => {
    if (editedSavedReply.title) {
      const isDuplicate = hasDuplicateTitle(editedSavedReply.title);
      setHasDuplicateTitleError(isDuplicate);
    }
  }, []); // Empty dependency array - only on mount

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedDuplicateCheck.cancel();
    };
  }, [debouncedDuplicateCheck]);

  const handleTitleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    const isEmpty = formValidationUtils.isInputEmpty(value);

    setHasEmptyTitleError(isEmpty);
    setEditedSavedReply({ ...editedSavedReply, title: value });

    // Only check for duplicates if the field is not empty
    if (!isEmpty) {
      debouncedDuplicateCheck(value);
    } else {
      // If empty, clear the duplicate error immediately
      setHasDuplicateTitleError(false);
      debouncedDuplicateCheck.cancel();
    }
  };

  const handleMessageInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const inputValue = event.target.value;
    setHasEmptyMessageError(formValidationUtils.isInputEmpty(inputValue));
    setEditedSavedReply({ ...editedSavedReply, reply: inputValue });
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <FormField
        control={
          <Input
            autoFocus={true}
            data-testid="saved-reply-input"
            fluid={true}
            maxLength={TITLE_FIELD_MAX_LENGTH}
            onChange={handleTitleInputChange}
            placeholder="Give it a title"
            size="md"
            type="text"
            value={editedSavedReply.title}
          />
        }
        description={<FormHelp error={hasDuplicateTitleError}>{titleFieldText}</FormHelp>}
        error={hasEmptyTitleError || hasDuplicateTitleError}
        label={<Label>Title</Label>}
        required={true}
        css={{
          marginBottom: 'default',
        }}
      />

      <FormField
        css={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}
        control={
          <Textarea
            css={{ flex: '1 1 auto' }}
            data-testid="saved-reply-textarea"
            maxLength={MESSAGE_FIELD_MAX_LENGTH}
            onChange={handleMessageInputChange}
            placeholder="Enter message text..."
            rows={7}
            value={editedSavedReply.reply}
          />
        }
        description={<FormHelp error={false}>{MESSAGE_FIELD_HELP_TEXT}</FormHelp>}
        error={hasEmptyMessageError}
        label={<Label>Message</Label>}
        required={true}
      />

      {hasFormError && (
        <InlineFeedback tone="critical" css={{ marginTop: 'default' }}>
          Sorry, something went wrong. Please try again.
        </InlineFeedback>
      )}
    </Box>
  );
}
